import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { IconCalendar, IconBell, IconChartBar, IconDots, IconHistory, IconLock, IconPlus, IconSearch, IconSend, IconLockPlus, IconDownload, IconArrowBackUp, IconSquareOff, IconTrash } from '@tabler/icons-react';
import { HeaderTool, Page, PageHeader } from '@hana/shell';
import { InvDock, useInvDock } from './InvDock';
import { useT } from '@hana/i18n';
import { Button, Skeleton, StateView, useToast } from '@hana/ui';
import { financeScreenState, useFinanceAccess } from '../finance/access';
import { norm } from '../finance/view';
import {
  ADJ_ROWS, AG_ROWS, DCF_ROWS, DRAFT_ROWS, INV_CUS, INV_LOCKED_PERIODS, ISS_ROWS, OUT_ROWS, QUE_ROWS,
  invDueLabel, invTabCounts, issFilter, issTotals, issVatNote, type InvTab,
} from './inv';
import { InvDrawer } from './InvDrawer';
import { BulkModal } from './BulkModal';
import { MisaModals, type MisaKind } from './MisaModals';
import { FOPT, fltActive, fltChips, fltCount, fltIds, type Flt, type FltKey } from './invf';
import a from './ar.module.css';
import s from './inv.module.css';
import './invoice.i18n';

// Mint-Invoice (1536, tab mặc định "Chờ xuất HĐ"). TIỀN → sau quyền `money`; chỉ đọc, nút ghi báo toast "Chưa mở ghi ở bản này".
type Tone = 'ok' | 'warn' | 'err' | 'pri' | 'ctl' | 'view';
interface Cell { n: ReactNode; sub?: string; subErr?: boolean; right?: boolean; strong?: boolean; link?: boolean; chip?: Tone; hist?: string; lock?: boolean }
interface Tbl { cols: readonly (readonly [string, number, boolean?])[]; rows: readonly (readonly [string, readonly Cell[]])[]; foot: readonly { t: string; err?: boolean; warn?: boolean; right?: boolean }[]; label: string; shown: string; tot: number }

const MAIN: readonly InvTab[] = ['queue', 'draft', 'dcf', 'issue'];
const MORE: readonly InvTab[] = ['out', 'adj', 'ag'];
const f1 = (x: number): string => x.toFixed(1).replace('.', ',');
const cn = (k: string): string => INV_CUS[k]?.[0] ?? '';
const mst = (k: string): string => `MST ${INV_CUS[k]?.[1] ?? ''}`;
const TONE: Readonly<Record<Tone, string>> = { ok: s.tOk ?? '', warn: s.tWarn ?? '', err: s.tErr ?? '', pri: s.tPri ?? '', ctl: s.tCtl ?? '', view: s.tView ?? '' };

type MxGo = 'toast' | 'vat' | 'mx' | 'mi';
const MX: readonly MxGo[] = ['toast', 'vat', 'mx', 'mi', 'toast', 'toast'];

function build(tab: InvTab, q: string, m: (x: string) => string, ok: (id: string) => boolean): Tbl {
  const n = norm(q.trim());
  const has = (...v: string[]) => !n || norm(v.join(' ')).includes(n);
  if (tab === 'issue') {
    const rows = issFilter(ISS_ROWS, q, norm).filter((r) => ok(r.dn));
    const t = issTotals(rows);
    return {
      label: 'Chờ xuất HĐ', tot: t.net + t.vat, shown: `Hiện ${rows.length} / ${ISS_ROWS.length} debit`,
      cols: [['Debit · Job', 196], ['Người được xuất (bill_to)', 232], ['Mốc hoàn thành', 156], ['Hạn phát hành', 148], ['Tiền hàng', 100, true], ['VAT', 90, true], ['Chi hộ', 80, true], ['Sale', 76]],
      rows: rows.map((r) => [r.dn, [
        { n: r.dn, sub: r.job, strong: true, link: true, hist: r.dn }, { n: cn(r.cus), sub: r.how, strong: true }, { n: r.milestone },
        { n: invDueLabel(r.days), chip: r.days < 0 ? 'err' : r.days === 0 ? 'warn' : 'ctl' },
        { n: m(f1(r.net)), right: true, strong: true }, { n: m(f1(r.vat)), sub: issVatNote(r), right: true }, { n: r.ch ? m(f1(r.ch)) : '—', right: true }, { n: r.sale },
      ]] as const),
      foot: [{ t: `${t.n} debit` }, { t: '' }, { t: '' }, { t: `${t.over} quá hạn`, err: true }, { t: m(f1(t.net)), right: true }, { t: m(f1(t.vat)), right: true }, { t: m(f1(t.ch)), right: true }, { t: '' }],
    };
  }
  if (tab === 'queue') {
    const rows = QUE_ROWS.filter((d) => ok(d[0]) && has(d[0], cn(d[1]), d[2], d[7]));
    return {
      label: 'Cần làm debit', tot: 0, shown: `Hiện ${rows.length} / ${QUE_ROWS.length} job`,
      cols: [['Job', 200], ['Khách (người được xuất)', 250], ['Kế toán', 96], ['Dòng phí', 90, true], ['Kiểm tra trước khi lập', 220], ['Sale', 90], ['Trạng thái', 120]],
      rows: rows.map((d) => [d[0], [
        { n: d[0], sub: `Đã chốt giá bán ${d[6]}`, strong: true, link: true }, { n: cn(d[1]), sub: d[5] ? 'Thiếu MST' : mst(d[1]), subErr: !!d[5], strong: true }, { n: d[2] }, { n: `${d[3]} dòng`, right: true },
        d[4] ? { n: `Thiếu giá ${d[4]} dòng`, chip: 'err' as const } : d[5] ? { n: 'Khách thiếu MST', chip: 'warn' as const } : { n: 'Đủ dữ liệu', chip: 'ok' as const },
        { n: d[7] }, { n: d[8], chip: d[8] === 'Đang làm' ? 'pri' as const : 'ctl' as const },
      ]] as const),
      foot: [{ t: `${rows.length} job` }, { t: '' }, { t: '' }, { t: '' }, { t: `${rows.filter((d) => d[4]).length} thiếu giá`, err: true }, { t: '' }, { t: '' }],
    };
  }
  if (tab === 'draft') {
    const rows = DRAFT_ROWS.filter((d) => ok(d[0]) && has(d[0], d[1], cn(d[2]), d[5]));
    return {
      label: 'Debit nháp', tot: rows.reduce((x, d) => x + d[3], 0), shown: `Hiện ${rows.length} / ${DRAFT_ROWS.length} debit nháp`,
      cols: [['Debit · Job', 196], ['Người được xuất', 232], ['Số tiền', 104, true], ['Tạo lúc', 150], ['Người lập', 96], ['Cách chia', 150], ['Khoá', 170]],
      rows: rows.map((d) => [d[0], [
        { n: d[0], sub: d[1], strong: true, link: true }, { n: cn(d[2]), strong: true, sub: mst(d[2]) }, { n: m(f1(d[3])), right: true, strong: true }, { n: d[4] }, { n: d[5] }, { n: d[6] }, { n: 'Nháp', chip: 'ctl' as const },
      ]] as const),
      foot: [{ t: `${rows.length} debit` }, { t: '' }, { t: m(f1(rows.reduce((x, d) => x + d[3], 0))), right: true }, { t: '' }, { t: '' }, { t: '' }, { t: '' }],
    };
  }
  if (tab === 'dcf') {
    const rows = DCF_ROWS.filter((d) => ok(d[0]) && has(d[0], d[1], cn(d[2]), d[6]));
    return {
      label: 'Chờ xác nhận', tot: rows.reduce((x, d) => x + d[3], 0), shown: `Hiện ${rows.length} / ${DCF_ROWS.length} debit`,
      cols: [['Debit · Job', 196], ['Khách', 232], ['Số tiền', 104, true], ['Đã gửi', 120], ['Nhắc', 90], ['Kênh', 130], ['Trạng thái', 180], ['Hạn', 90]],
      rows: rows.map((d) => [d[0], [
        { n: d[0], sub: d[1], strong: true, link: true }, { n: cn(d[2]), strong: true, sub: mst(d[2]) }, { n: m(f1(d[3])), right: true, strong: true }, { n: d[4] }, { n: `${d[5]} lần` }, { n: d[6] },
        { n: d[7], chip: d[7].startsWith('Khách yêu cầu') ? 'err' as const : 'warn' as const }, { n: d[8] },
      ]] as const),
      foot: [{ t: `${rows.length} debit` }, { t: '' }, { t: m(f1(rows.reduce((x, d) => x + d[3], 0))), right: true }, { t: '' }, { t: '' }, { t: '' }, { t: '' }, { t: '' }],
    };
  }
  if (tab === 'out') {
    const rows = OUT_ROWS.filter((d) => ok(d[0]) && has(d[0], cn(d[2]), mst(d[2])));
    return {
      label: 'Đã xuất', tot: rows.reduce((x, d) => x + d[3] + d[4], 0), shown: `Hiện ${rows.length} / 23 hoá đơn`,
      cols: [['Số hoá đơn', 170], ['Ngày', 116], ['Người mua', 260], ['Tiền hàng', 110, true], ['VAT', 90, true], ['Tổng', 110, true], ['Ký số', 100], ['Gửi khách', 180]],
      rows: rows.map((d) => [d[0], [
        { n: d[0], strong: true, link: true, lock: INV_LOCKED_PERIODS.includes(d[7]) }, { n: d[1] }, { n: cn(d[2]), sub: mst(d[2]), strong: true }, { n: m(f1(d[3])), right: true }, { n: m(f1(d[4])), right: true }, { n: m(f1(d[3] + d[4])), right: true, strong: true },
        { n: d[5], chip: 'ok' as const }, { n: d[6], chip: d[6] === 'Khách đã xem' ? 'view' as const : 'ctl' as const },
      ]] as const),
      foot: [{ t: `${rows.length} / 23 hoá đơn` }, { t: '' }, { t: '' }, { t: m(f1(rows.reduce((x, d) => x + d[3], 0))), right: true }, { t: m(f1(rows.reduce((x, d) => x + d[4], 0))), right: true }, { t: m(f1(rows.reduce((x, d) => x + d[3] + d[4], 0))), right: true }, { t: '' }, { t: '' }],
    };
  }
  if (tab === 'adj') {
    const rows = ADJ_ROWS.filter((d) => ok(d[0]) && has(d[0], d[1], d[2], cn(d[3]), d[4]));
    return {
      label: 'Điều chỉnh / Thay thế', tot: rows.reduce((x, d) => x + d[5], 0), shown: `Hiện ${rows.length} / ${ADJ_ROWS.length} phiếu`,
      cols: [['Phiếu', 136], ['Loại', 150], ['HĐ gốc', 150], ['Người mua', 210], ['Lý do', 230], ['Chênh', 96, true], ['Người lập', 84], ['Trạng thái', 164]],
      rows: rows.map((d) => [d[0], [
        { n: d[0], strong: true, link: true }, { n: d[1] }, { n: d[2], lock: d[2] === '1/C26THL/2402' || d[2] === '1/C26THL/2398' }, { n: cn(d[3]), strong: true }, { n: d[4] },
        { n: d[5] === 0 ? '—' : `${d[5] > 0 ? '+' : '−'}${m(f1(Math.abs(d[5])))}`, right: true, strong: true }, { n: d[6] }, { n: d[7], chip: d[7] === 'Chờ KTT duyệt' ? 'warn' as const : 'ok' as const },
      ]] as const),
      foot: [{ t: `${rows.length} phiếu` }, { t: '' }, { t: '' }, { t: '' }, { t: '' }, { t: '', right: true }, { t: '' }, { t: `${rows.filter((d) => d[7] === 'Chờ KTT duyệt').length} chờ KTT`, warn: true }],
    };
  }
  const rows = AG_ROWS.filter((d) => ok(d[0]) && has(d[0], d[1], d[2], d[3]));
  const usd = (x: number) => `${x < 0 ? '−' : ''}${Math.abs(x).toLocaleString('en-US')}`;
  return {
    label: 'Đại lý', tot: rows.reduce((x, d) => x + d[4], 0) * 25400 / 1e6, shown: `Hiện ${rows.length} / ${AG_ROWS.length} chứng từ`,
    cols: [['Số', 160], ['Loại', 120], ['Đại lý', 250], ['Job', 180], ['USD', 100, true], ['SOA', 130], ['Trạng thái', 220]],
    rows: rows.map((d) => [d[0], [
      { n: d[0], strong: true, link: true }, { n: d[1] }, { n: d[2], strong: true }, { n: d[3], link: true }, { n: m(usd(d[4])), right: true, strong: true }, { n: d[5] },
      { n: d[6], chip: d[6].includes('collect') ? 'warn' as const : d[6] === 'Đã cấn trừ' || d[6] === 'Đã thanh toán' ? 'ok' as const : 'ctl' as const },
    ]] as const),
    foot: [{ t: `${rows.length} chứng từ` }, { t: '' }, { t: '' }, { t: '' }, { t: `Ròng ${m(usd(rows.reduce((x, d) => x + d[4], 0)))} USD`, right: true }, { t: '' }, { t: '' }],
  };
}

// Thanh hàng loạt (board: `bulk` theo tab). Ghi chưa mở → toast.
const BULK: Readonly<Record<InvTab, readonly string[]>> = {
  queue: ['mk', 'ret'], draft: ['lock', 'send'], dcf: ['remind'], issue: ['issue', 'pdfd'], out: ['pdf'], adj: [], ag: [],
};
const BULK_ICON: Readonly<Record<string, ReactNode>> = {
  'mk': <IconPlus size={16} aria-hidden="true" />, 'ret': <IconArrowBackUp size={16} aria-hidden="true" />, 'lock': <IconLockPlus size={16} aria-hidden="true" />,
  'send': <IconSend size={16} aria-hidden="true" />, 'remind': <IconBell size={16} aria-hidden="true" />, 'issue': <IconSend size={16} aria-hidden="true" />,
  'pdfd': <IconDownload size={16} aria-hidden="true" />, 'pdf': <IconDownload size={16} aria-hidden="true" />,
};
const amtOf = (tab: InvTab, id: string): number => {
  if (tab === 'issue') { const r = ISS_ROWS.find((x) => x.dn === id); return r ? r.net + r.vat : 0; }
  if (tab === 'draft') return DRAFT_ROWS.find((x) => x[0] === id)?.[3] ?? 0;
  if (tab === 'dcf') return DCF_ROWS.find((x) => x[0] === id)?.[3] ?? 0;
  if (tab === 'out') { const r = OUT_ROWS.find((x) => x[0] === id); return r ? r[3] + r[4] : 0; }
  if (tab === 'adj') return ADJ_ROWS.find((x) => x[0] === id)?.[5] ?? 0;
  return 0;
};

export function Invoice() {
  const t = useT();
  const toast = useToast();
  const access = useFinanceAccess();
  const state = financeScreenState(access);
  const [tab, setTab] = useState<InvTab>('issue');
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState<readonly string[]>([]);
  const pick = (id: string) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const goTab = (k: InvTab) => { setTab(k); setPicked([]); };
  const [mx, setMx] = useState(false);
  const [dense, setDense] = useState(false);
  const [dr, setDr] = useState(false);
  const [bulkM, setBulkM] = useState(false);
  const [misa, setMisa] = useState<MisaKind>(null);
  const [flt, setFlt] = useState<Flt>({});
  const [fp, setFp] = useState(false);
  const fpRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!fp) return;
    const off = (e: MouseEvent) => { if (!fpRef.current?.contains(e.target as Node)) setFp(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setFp(false); };
    document.addEventListener('mousedown', off); document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', off); document.removeEventListener('keydown', esc); };
  }, [fp]);
  const actF = fltActive(flt);
  const setF = (k: FltKey) => setFlt({ ...flt, [k]: !flt[k] });
  const mxRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!mx) return;
    const off = (e: MouseEvent) => { if (!mxRef.current?.contains(e.target as Node)) setMx(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setMx(false); };
    document.addEventListener('mousedown', off); document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', off); document.removeEventListener('keydown', esc); };
  }, [mx]);
  const noWrite = () => toast({ msg: t('iv.noWrite') });
  const ready = state === 'ready';
  const dock = useInvDock();
  const counts = invTabCounts();
  const m = (x: string) => (access.money ? x : '•••••');
  const tb = useMemo(() => { const ids = fltIds(tab, flt); return build(tab, q, m, (id) => ids.has(id)); }, [tab, q, flt, access.money]); // eslint-disable-line react-hooks/exhaustive-deps
  const total = { queue: QUE_ROWS, draft: DRAFT_ROWS, dcf: DCF_ROWS, issue: ISS_ROWS, out: OUT_ROWS, adj: ADJ_ROWS, ag: AG_ROWS }[tab].length;
  const isMore = MORE.includes(tab);

  const lockNote = (<div className={s.note}>
              <IconLock size={16} aria-hidden="true" />
              <span title={t(`iv.note.${tab}`)}><b>{t('iv.lockNote')}</b></span>
              <button type="button" className={s.lockBtn} aria-label={t('iv.lockAria')} title={t('iv.lockAria')} onClick={() => toast({ msg: t('iv.lockMsg') })}><IconLock size={16} aria-hidden="true" /></button>
            </div>);
  return (
    <Page kind="table" dock={dock.wide && ready ? <InvDock dock={dock} onNote={noWrite} top={lockNote} /> : undefined}>
      <PageHeader
        title={t('iv.title')}
        tabs={
          <div className={a.tabs} role="tablist" aria-label={t('iv.tabsAria')}>
            {MAIN.map((k) => (
              <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => goTab(k)}>{t(`iv.tab.${k}`)}{ready && <span>{counts[k]}</span>}</button>
            ))}
            <button type="button" aria-haspopup="menu" aria-expanded="false" aria-pressed={isMore} onClick={() => goTab(isMore ? 'issue' : 'out')}>{t('iv.more')}</button>
          </div>
        }
        actions={
          <>
            <HeaderTool label={t('iv.bellAria')} icon={IconBell} n={4} onClick={() => toast({ msg: t('iv.bellMsg') })} />
            <HeaderTool label={t('iv.dockAria')} icon={IconChartBar} onClick={() => dock.toggle('an')} />
            <div ref={mxRef} className={s.mxWrap}>
              <HeaderTool label={t('iv.mxAria')} icon={IconDots} active={mx} onClick={() => setMx(!mx)} />
              {mx && (
                <div role="menu" aria-label={t('iv.mxAria')} className={s.mx}>
                  <button type="button" role="menuitem" onClick={() => { setMx(false); setDense(!dense); }}><span>{t('iv.density', { a: t(dense ? 'iv.dense.toStd' : 'iv.dense.toCmp') })}</span><small>{t('iv.densityS', { a: dense ? '40 → 48' : '48 → 40' })}</small></button>
                  {MX.map((go, i) => <button key={i} type="button" role="menuitem" onClick={() => { setMx(false); if (go === 'toast') toast({ msg: t(`iv.mx.${i}m`) }); else setMisa(go); }}><span>{t(`iv.mx.${i}`)}</span><small>{t(`iv.mx.${i}s`)}</small></button>)}
                </div>
              )}
            </div>
            <Button variant="primary" icon={<IconPlus size={16} aria-hidden="true" />} aria-haspopup="dialog" onClick={() => setDr(true)}>{t('iv.create')}</Button>
          </>
        }
      />
      <div className={a.page}>
        <StateView state={state} skeleton={<Skeleton rows={9} />}>
          <>
            {picked.length > 0 ? (
              <div className={`${a.bar} ${s.bulk}`} role="toolbar" aria-label={t('iv.bulkAria')}>
                <b>{t('iv.picked', { n: picked.length })}</b>
                {tab !== 'queue' && <span>· {m(f1(picked.reduce((x, id) => x + amtOf(tab, id), 0)))} tr ₫</span>}
                <div className={s.bulkAct}>
                  {BULK[tab].map((k) => <button key={k} type="button" onClick={k === 'issue' ? () => setBulkM(true) : noWrite}>{BULK_ICON[k]}{t(`iv.bk.${k}`)}</button>)}
                  <button type="button" className={s.bulkOff} onClick={() => setPicked([])}><IconSquareOff size={16} aria-hidden="true" />{t('iv.unpick')}</button>
                </div>
              </div>
            ) : (
            <div className={a.bar}>
              <label className={a.search} style={{ width: 300 }}>
                <IconSearch size={16} aria-hidden="true" />
                <input aria-label={t('iv.searchAria')} placeholder={t('iv.searchPh')} value={q} onChange={(e) => setQ(e.target.value)} />
              </label>
              <span className={a.ctl}><IconCalendar size={16} aria-hidden="true" />{t('iv.period')}</span>
              {fltChips(flt).map((c) => (
                <span key={c.key} className={s.fchip}>{c.label}<button type="button" aria-label={t('iv.unfilter', { a: c.label })} onClick={() => setFlt(c.key === 'more' ? {} : { ...flt, [c.key]: false })}>×</button></span>
              ))}
              <div ref={fpRef} className={s.mxWrap} style={{ marginLeft: 0 }}>
                <button type="button" className={`${a.ctl2} ${fp ? 'on' : ''}`} aria-haspopup="true" aria-expanded={fp} onClick={() => setFp(!fp)}><IconPlus size={16} aria-hidden="true" />{t('iv.filter')}</button>
                {fp && (
                  <div role="menu" aria-label={t('iv.filterAria')} className={s.fp}>
                    {FOPT.map(([k, label]) => (
                      <button key={k} type="button" role="menuitemcheckbox" aria-checked={!!flt[k]} className={flt[k] ? s.fpOn : ''} onClick={() => setF(k)}>
                        <span className={s.fpBox}>{flt[k] ? '✓' : ''}</span><span className={s.fpLb}>{label}</span><small>{fltCount(tab, k)}</small>
                      </button>
                    ))}
                    <div className={s.fpPre}>{t('iv.match', { n: tb.rows.length })}</div>
                  </div>
                )}
              </div>
              {actF.length > 0 && <button type="button" className={s.fclr} onClick={() => { setFlt({}); setQ(''); }}><IconTrash size={16} aria-hidden="true" />{t('iv.clear')}</button>}
              <div className={a.tots}>
                <span>{t('iv.total')} <b>{m(f1(tb.tot))} tr</b></span><span>{t('iv.showing')} <b>{tb.rows.length} / {total}</b></span>
                </div>
            </div>
            )}
            {!(dock.wide && dock.open) && lockNote}
            {tb.rows.length === 0 ? (
              <StateView state="empty" title={t('iv.emptyT')} desc={t('iv.emptyD')} />
            ) : (
              <section className={a.card} aria-label={tb.label}>
                <table className={`${a.tbl} ${s.tbl}${dense ? ` ${s.dense}` : ''}`}>
                  <colgroup><col style={{ width: 44 }} />{tb.cols.map(([l, w]) => <col key={l} style={{ width: w }} />)}</colgroup>
                  <thead>
                    <tr><th><input type="checkbox" aria-label={t('iv.pickAll')} checked={picked.length > 0 && picked.length === tb.rows.length} onChange={(e) => setPicked(e.target.checked ? tb.rows.map(([id]) => id) : [])} /></th>{tb.cols.map(([l, , r]) => <th key={l} className={r ? a.r : ''}>{l}</th>)}</tr>
                  </thead>
                  <tbody>
                    {tb.rows.map(([id, cells]) => (
                      <tr key={id} className={picked.includes(id) ? s.sel : ''}>
                        <td><input type="checkbox" aria-label={t('iv.pickOne', { a: id })} checked={picked.includes(id)} onChange={() => pick(id)} /></td>
                        {cells.map((c, i) => (
                          <td key={i} className={c.right ? a.r : ''}>
                            {c.chip ? <span className={`${a.flag} ${TONE[c.chip]}`}><i />{c.n}</span> : <span className={`${c.strong ? s.b : ''} ${c.link ? s.link : ''}`}>{c.n}</span>}
                            {c.hist && <a className={s.hist} href={`/ezy/ar?money=view&q=${c.hist}`} aria-label={t('iv.histAria', { a: c.hist })} title={t('iv.hist')}><IconHistory size={14} aria-hidden="true" /></a>}
                            {c.lock && <span className={s.lk} title={t('iv.locked')}><IconLock size={13} role="img" aria-label={t('iv.locked')} /></span>}
                            {c.sub && <div className={`${s.sub} ${c.subErr ? s.err : ''}`}>{c.sub}</div>}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr><td />{tb.foot.map((f, i) => <td key={i} className={`${f.right ? a.r : ''} ${f.err ? s.err : ''} ${f.warn ? s.warnTx : ''}`}>{f.t}</td>)}</tr>
                  </tfoot>
                </table>
                <div className={a.foot}><span>{tb.shown}</span></div>
              </section>
            )}
          </>
        </StateView>
      </div>
      <MisaModals kind={misa} money={access.money} onClose={() => setMisa(null)} />
      <InvDrawer open={dr} onClose={() => setDr(false)} />
      <BulkModal ids={bulkM && tab === 'issue' ? picked : null} total={`${m(f1(picked.reduce((x, id) => x + amtOf('issue', id), 0)))} tr ₫`} onClose={() => setBulkM(false)} />
    </Page>
  );
}
